/**
 * Authentication composable — thin wrapper over `supabase.auth`.
 *
 * State (`session`/`user`) is module-level so it is shared by every component
 * and by the router navigation guard. Init runs once on first import: it reads
 * the current session and subscribes to auth changes.
 */
import { computed, ref } from 'vue'
// Types only — erased at build, so this does NOT pull the SDK into the bundle.
import type { Session, AuthError } from '@supabase/supabase-js'

// Perf (INC-099): the Supabase client is loaded via dynamic import() rather than
// a static import. useAuth is imported by AppNav (global header) and LandingView,
// both of which sit in the initial chunk — a static import would drag the whole
// @supabase/supabase-js SDK (auth-js + storage-js, ~230 KiB) into the render-
// blocking main bundle and inflate LCP on the landing page. Dynamic import splits
// it into its own async chunk, fetched off the critical path. The 7 lib/* modules
// keep their static `import { supabase }` — they are only reachable from lazy
// route chunks, so the SDK naturally lands there, never in the initial bundle.
const loadClient = () => import('@/utils/supabase').then((m) => m.supabase)

const session = ref<Session | null>(null)
const user = computed(() => session.value?.user ?? null)

/** Resolves once the initial session has been loaded (used by the router guard). */
let resolveReady: () => void
const ready = new Promise<void>((resolve) => {
  resolveReady = resolve
})

let initialized = false
async function init() {
  if (initialized) return
  initialized = true
  try {
    const supabase = await loadClient()
    supabase.auth
      .getSession()
      .then(({ data }) => {
        session.value = data.session
      })
      .finally(() => resolveReady())
    supabase.auth.onAuthStateChange((_event, newSession) => {
      session.value = newSession
    })
  } catch {
    // SDK chunk failed to load — unblock the router guard so the app still boots.
    resolveReady()
  }
}
if (!import.meta.env.SSR) {
  // Perf (INC-099): defer the initial session fetch (and thus the ~200 KiB SDK
  // chunk download) to idle time so it never competes with the landing page's
  // LCP resources. Authed routes don't wait for idle — the router guard calls
  // init() eagerly (see installGuard) before awaiting `ready`, so protected
  // pages still gate correctly on the real session.
  const scheduleInit = () => void init()
  if (typeof requestIdleCallback === 'function') {
    requestIdleCallback(scheduleInit, { timeout: 3000 })
  } else {
    setTimeout(scheduleInit, 300)
  }
}

/**
 * `error` carries Supabase failures. `needsConfirmation` is true when the call
 * succeeded but produced NO session (typically email-confirmation is enabled):
 * without a session there is no JWT, so any later DB write hits RLS as anon.
 * Callers should treat this as "not really logged in".
 */
type AuthResult = { error: AuthError | null; needsConfirmation?: boolean }

async function signUp(email: string, password: string): Promise<AuthResult> {
  const supabase = await loadClient()
  const { data, error } = await supabase.auth.signUp({ email, password })
  return { error, needsConfirmation: !error && !data.session }
}

async function signIn(email: string, password: string): Promise<AuthResult> {
  const supabase = await loadClient()
  const { data, error } = await supabase.auth.signInWithPassword({ email, password })
  return { error, needsConfirmation: !error && !data.session }
}

async function signOut(): Promise<AuthResult> {
  const supabase = await loadClient()
  const { error } = await supabase.auth.signOut()
  return { error }
}

export function useAuth() {
  return { session, user, ready, ensureReady: init, signUp, signIn, signOut }
}
